import { configureCdnOrigins, rehomeCdnUrlToDocumentOrigin } from '@abax/platform';
/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { rehomeCdnUrlForDevProxy } from '@/utils/module-federation/dev-cdn-proxy-url';
import shellEnv from '../../env.schema';

type EnvironmentVariables = Readonly<ReturnType<typeof shellEnv.parse>>;

type HubSpotChat = {
  scriptUrl: string;
  identificationToken: string;
  userEmail: string;
};

interface HsConversationsSettings {
  loadImmediately?: boolean;
  inlineEmbedSelector?: string;
  enableWidgetCookieBanner?: boolean;
  disableAttachment?: boolean;
  identificationEmail?: string;
  identificationToken?: string;
}

declare global {
  interface Window {
    ['shell/__env__']: EnvironmentVariables;
    hubSpotChat: HubSpotChat | null;
    hsConversationsSettings: HsConversationsSettings;
    HubSpotConversations?: any | null;
    hsConversationsOnReady?: any | null;
  }
}

/**
 * Read-only legacy view of Shell's own configuration.
 * Before loadConfig resolves, missing values remain undefined as on the original host API.
 * MFE configuration is owned by each producer and never replaces this snapshot.
 */
const config = new Proxy({} as EnvironmentVariables, {
  get(_target, key: keyof EnvironmentVariables): unknown {
    try {
      return shellEnv.all()[key];
    } catch {
      return undefined;
    }
  },
  set(): boolean {
    return false;
  },
  has(_target, key: keyof EnvironmentVariables): boolean {
    try {
      return Object.prototype.hasOwnProperty.call(shellEnv.all(), key);
    } catch {
      return false;
    }
  },
  ownKeys(): string[] {
    try {
      return Object.keys(shellEnv.all());
    } catch {
      return [];
    }
  },
  getOwnPropertyDescriptor(
    _target,
    key: keyof EnvironmentVariables,
  ): PropertyDescriptor | undefined {
    try {
      const snapshot = shellEnv.all();
      if (!Object.prototype.hasOwnProperty.call(snapshot, key)) return undefined;
      return { enumerable: true, configurable: true, value: snapshot[key], writable: false };
    } catch {
      return undefined;
    }
  },
});

window['shell/__env__'] = config;

export function isAutomileBrand(): boolean {
  return (
    !!config.AUTOMILE_HOST &&
    window.location.hostname.toLowerCase() === config.AUTOMILE_HOST.toLowerCase()
  );
}

// Picks the Automile-branded value over the default one when the current
// page is served from AUTOMILE_HOST and a branded value is actually configured.
//
// Deliberately NOT exported: every branded key gets a named accessor below.
// A call site that reads the raw `config.AUTH_AUTHORITY` instead silently
// bypasses brand switching, and that is exactly how `getAuthority()` on the
// public shell-frontend-api was missed the first time around.
function resolveBrandedValue(defaultValue: string, automileValue: string): string {
  return isAutomileBrand() && automileValue ? automileValue : defaultValue;
}

/**
 * Identity-server authority for the brand serving the current page.
 *
 * Read this instead of `config.AUTH_AUTHORITY` anywhere the value is used to
 * reach the identity server. Consumed by the OIDC `UserManager` (sign-in,
 * silent-renew re-auth, sign-out), by `switchAccount()`'s `/account/switch`
 * URL, and by `api.auth.getAuthority()` on the public shell-frontend-api.
 */
export function getAuthority(): string {
  return resolveBrandedValue(config.AUTH_AUTHORITY, config.AUTH_AUTHORITY_AUTOMILE);
}

/**
 * Post-logout landing page for the brand serving the current page.
 *
 * Read this instead of `config.DEFAULT_SIGN_OUT_REDIRECT_URL`.
 */
export function getDefaultSignOutRedirectUrl(): string {
  return resolveBrandedValue(
    config.DEFAULT_SIGN_OUT_REDIRECT_URL,
    config.DEFAULT_SIGN_OUT_REDIRECT_URL_AUTOMILE,
  );
}

/**
 * One-shot boot diagnostic, emitted only when the Automile brand is active.
 *
 * Brand switching is driven by per-environment runtime config, and every way
 * it can be misconfigured fails silently: the `*_AUTOMILE` value is ignored
 * and Automile customers land on the ABAX-branded identity server with
 * nothing anywhere to say so. Two distinct failures are covered here:
 *
 * - Variable unset in an environment. `fill-variables.mjs` only substitutes a
 *   key when the matching `K8S_SECRET_*` env var exists, so an unset variable
 *   leaves whatever `public/env-config.json` shipped in the image.
 * - That shipped default is the *staging* value, not an empty string, so it
 *   is not caught by `resolveBrandedValue`'s empty check — it simply wins. Logging
 *   the resolved URLs makes a stale cross-environment value obvious in a boot
 *   log (an `ext-stg` host on a production pod).
 *
 * Called from `loadConfig()` rather than from `resolveBrandedValue()`: the
 * accessors it reads go through `resolveBrandedValue()` themselves, so calling
 * it from there would recurse.
 */
function logBrandDiagnostics(): void {
  if (!isAutomileBrand()) return;

  const unset = (
    [
      ['AUTH_AUTHORITY_AUTOMILE', config.AUTH_AUTHORITY_AUTOMILE],
      ['DEFAULT_SIGN_OUT_REDIRECT_URL_AUTOMILE', config.DEFAULT_SIGN_OUT_REDIRECT_URL_AUTOMILE],
    ] as const
  )
    .filter(([, value]) => !value)
    .map(([key]) => key);

  const resolved =
    `Automile brand active for ${window.location.hostname} — ` +
    `authority: ${getAuthority()}, sign-out: ${getDefaultSignOutRedirectUrl()}`;

  if (unset.length) {
    // eslint-disable-next-line no-console
    console.warn(
      `[config] ${resolved}. Falling back to the ABAX-branded default for: ` +
        `${unset.join(', ')} — set the matching K8S_SECRET_* variable for this environment.`,
    );
    return;
  }

  // eslint-disable-next-line no-console
  console.info(`[config] ${resolved}`);
}

function normalizeConfig(
  parsed: ReturnType<typeof shellEnv.parse>,
): ReturnType<typeof shellEnv.parse> {
  // Dev re-homing precedes the common CDN normalization, as in the existing host boot path.
  parsed.ABAX_UI_REMOTE_URL = rehomeCdnUrlForDevProxy(parsed.ABAX_UI_REMOTE_URL);
  parsed.ABAX_UI_MANIFEST_URL = rehomeCdnUrlForDevProxy(parsed.ABAX_UI_MANIFEST_URL);
  configureCdnOrigins(parsed.ABAX_CDN_ORIGINS);
  parsed.ABAX_UI_REMOTE_URL = rehomeCdnUrlToDocumentOrigin(parsed.ABAX_UI_REMOTE_URL);
  parsed.ABAX_UI_MANIFEST_URL = rehomeCdnUrlToDocumentOrigin(parsed.ABAX_UI_MANIFEST_URL);
  return parsed;
}

export async function loadConfig(): Promise<void> {
  const res = await fetch('env-config.json', { credentials: 'same-origin', cache: 'no-store' });
  if (!res.ok) {
    throw new Error(
      `[@abax/shell] Failed to load env-config.json: ${res.status} ${res.statusText}`,
    );
  }
  const raw: unknown = await res.json();
  shellEnv.initialize(raw, { prepare: normalizeConfig });
  logBrandDiagnostics();
}

export default config;
